import { Avatar, Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { AIFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { assertUnreachable } from '../../../../util/guards';
import { useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { useIsAiChatbot } from '../../../common/hooks/useIsAiChatbot';
import { selectIsLiveChat, selectIsUserEngaged } from '../../../common/selectors/chat';
import { selectOnlineAgent } from '../../../common/selectors/header';
import { Page } from '../../../common/types';
import { experiments } from '../../../experiments';
import { renderModel } from '../../../util/renderModel';
import { headerDataHooks } from './Header.dataHooks';
import { HeaderTitleTypography } from './HeaderTitleTypography';
import s from './styles.scss';

const isChatbotDisabled = renderModel.isChatbotDisabled();
const searchWithKnowledgeAgent = experiments.is('specs.helpwidget.searchWithKnowledgeAgent', 'true');

export const HeaderTitle: FC = () => {
  const { t } = useTranslation();
  const currentPage = useCurrentPage();
  const isLiveChat = useSelector(selectIsLiveChat);
  const isUserEngaged = useSelector(selectIsUserEngaged);
  const agent = useSelector(selectOnlineAgent);
  const isAiChatbot = useIsAiChatbot();
  const isHomePage = currentPage === Page.Home;

  const headerTitleKey = useMemo(() => {
    switch (currentPage) {
      case Page.Home:
        return 'help_center_header_title_widget_home';
      case Page.Chatbot:
        return isAiChatbot ? 'widget_title_ai_chatbot' : 'widget_title';
      case Page.Search:
        return 'help_center_header_title_widget_search';
      case Page.Categories:
      case Page.Category:
      case Page.Article:
        return 'help_center_header_title_widget';
      case Page.RequestSuccessManagerForm:
        return 'request_success_manager_form_page_title';
      default:
        assertUnreachable(currentPage);
    }
  }, [currentPage, isAiChatbot]);

  const title = useMemo(() => {
    const isChatbotPage = currentPage === Page.Chatbot;
    const messagesIconVisible = isUserEngaged && !isChatbotDisabled;
    const showSubtitle = searchWithKnowledgeAgent && isHomePage && !isLiveChat && !messagesIconVisible;

    if (isChatbotPage && isLiveChat && agent) {
      return (
        <Box direction="horizontal" gap="SP2" verticalAlign="middle" overflow="hidden">
          <Avatar
            size="size30"
            name={agent.fullName}
            imgProps={{ src: agent.profileImage }}
            dataHook={headerDataHooks.titleAvatar()}
          />
          <HeaderTitleTypography dataHook={headerDataHooks.titleText()}>
            {agent.fullName}
          </HeaderTitleTypography>
        </Box>
      );
    }

    return (
      <Box verticalAlign="middle">
        {isChatbotPage && isAiChatbot && (
          <Text light>
            <Box>
              <AIFilled />
            </Box>
          </Text>
        )}
        <Box direction="vertical">
          <HeaderTitleTypography dataHook={headerDataHooks.titleText()}>
            {t<TLocalesKeys>(headerTitleKey)}
          </HeaderTitleTypography>
          {showSubtitle && (
            <Text size="tiny" light className={s.subTitle}>
              {t<TLocalesKeys>('widget-search-help-note')}
            </Text>
          )}
        </Box>
      </Box>
    );
  }, [currentPage, isUserEngaged, isLiveChat, agent, headerTitleKey, isAiChatbot, t]);

  return (
    <Box direction="vertical" overflow="hidden">
      {title}
    </Box>
  );
};
